import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/shared/ui/Alert';
import { Card } from '@/shared/ui/Card';
import { DataTable } from '@/shared/ui/DataTable';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import { useNodeEdges } from '../api/lineage';
import type { AsOf, Edge, LineageLens } from '../api/lineage';
import { confidencePercent, formatInstant, sourceKey } from '../model/presentation';
import { assertionsOf, filterByLens, runLabelFor } from '../model/provenance';

/* Where this dataset's lineage came from, edge by edge.
 *
 * **This is the provenance half of the screen, and it answers a question the graph cannot: why does
 * the platform think these two tables are connected?** The canvas draws nodes and connectors; it
 * says nothing about whether a flow was declared by the job that ran it, read out of a warehouse's
 * own lineage catalogue, parsed from SQL, or guessed by a model. `docs/design/LINEAGE.md` puts that
 * distinction at the centre of D32 — *"the source is on the record for ever so an auditor can ask
 * which edges a model invented"* — and until now nothing rendered it.
 *
 * **It consumes `useNodeEdges`, which has existed since the module was built and was wired to
 * nothing.** `model/graph.ts` records that gap in its own words: the hook is *"one node's real
 * neighbours, straight from `lineage.edge` — the exact answer this approximation is not"*, and the
 * panel that read it was removed when the screen was cut back to the canvas. So the canvas has been
 * showing an adjacent-depth approximation with no way for a reader to check it. This table is that
 * way: it is the exact edge list, unfiltered, including the `needs_review` edges every impact answer
 * excludes.
 *
 * **What "native" means here, stated because the alternative reading is the tempting one.** These
 * are the assertions *this platform has stored*, labelled with the provider each came from — not a
 * live re-read of Snowflake or Databricks. A live read would dial a customer's warehouse on every
 * page view, which hard rule 14 exists to bound and which `DatasetDetail`'s profile button records
 * the cost of. The distinction is on the panel rather than left to be discovered: what the source
 * said, as we recorded it, with when we saw it.
 */

function ConfidenceCell({ edge }: { readonly edge: Edge }): ReactElement {
  const { t } = useTranslation();
  const percent = confidencePercent(edge);

  /* Blank rather than 0% or 100% where the concept does not apply. A facet carries no confidence
     because the producing job simply stated the flow, and a number there would invite a reader to
     treat a plain statement as a probability. */
  if (percent === null) {
    return <span className="lq-dim">{t('lineage.edges.notProbabilistic')}</span>;
  }
  return <>{`${String(percent)}%`}</>;
}

export function NativeLineagePanel({
  nodeId,
  asOf,
  lens,
}: {
  readonly nodeId: string;
  readonly asOf: AsOf;
  readonly lens: LineageLens;
}): ReactElement {
  const { t } = useTranslation();
  /* Both directions, because provenance is not directional: "what fed this" and "what this feeds"
     are both assertions somebody made, and a reader auditing one wants the same columns for the
     other. Two reads rather than one because the route is direction-scoped. */
  const upstream = useNodeEdges(nodeId, 'upstream', asOf);
  const downstream = useNodeEdges(nodeId, 'downstream', asOf);

  if (upstream.isPending || downstream.isPending) {
    return (
      <Card title={t('lineage.native.title')} hint={t('lineage.native.hint')}>
        <SkeletonRows rows={3} columns={4} />
      </Card>
    );
  }

  if (upstream.isError || downstream.isError) {
    return (
      <Card title={t('lineage.native.title')} hint={t('lineage.native.hint')}>
        {/* A failed read is reported as a failure of the request. Rendering "no assertions" would
            state something about the customer's graph that nothing measured — the class this
            codebase records at four screens. */}
        <Alert tone="error" title={t('lineage.native.errorTitle')}>
          {t('lineage.native.errorBody')}
        </Alert>
      </Card>
    );
  }

  const allAssertions = assertionsOf(upstream.data.edges, downstream.data.edges);
  const assertions = filterByLens(allAssertions, lens);

  return (
    <Card title={t('lineage.native.title')} hint={t('lineage.native.hint')}>
      {allAssertions.length === 0 ? (
        <p className="lq-muted" style={{ margin: 0 }}>
          {t('lineage.native.none')}
        </p>
      ) : assertions.length === 0 ? (
        /* There is lineage here, just none under the lens currently selected — a different fact
           from `lineage.native.none`, and conflating them would tell a reader nothing touches this
           node when Combined would show otherwise. */
        <p className="lq-muted" style={{ margin: 0 }}>
          {t(
            lens === 'native' ? 'lineage.lens.emptyNative' : 'lineage.lens.emptyPlatform',
          )}
        </p>
      ) : (
        <DataTable
          caption={t('lineage.native.caption')}
          columns={[
            { key: 'flow', label: t('lineage.native.column.flow') },
            { key: 'provider', label: t('lineage.native.column.provider') },
            {
              key: 'confidence',
              label: t('lineage.native.column.confidence'),
              numeric: true,
            },
            { key: 'observed', label: t('lineage.native.column.observed') },
          ]}
        >
          {assertions.map((assertion) => {
            const edge = assertion.edge;
            return (
              <tr key={edge.id}>
                <td>
                  <span className="lq-mono">{edge.upstream_name}</span>
                  <span aria-hidden="true" className="lq-dim">
                    {' → '}
                  </span>
                  <span className="lq-mono">{edge.downstream_name}</span>
                  {/* A `needs_review` edge is excluded from every impact answer, so it is in this
                      table and not in the graph. Saying so is the point: without it a reader
                      comparing the two would think one of them had lost an edge. */}
                  {!edge.is_traversable && (
                    <div className="lq-dim">{t('lineage.native.notTraversable')}</div>
                  )}
                </td>
                <td>
                  <StatusBadge
                    tone={assertion.native ? 'ok' : 'info'}
                    label={t(sourceKey(edge.source))}
                    title={t(
                      assertion.native
                        ? 'lineage.native.providerNative'
                        : 'lineage.native.providerPushed',
                    )}
                  />
                  <div className="lq-dim">
                    {t(runLabelFor(edge).key, runLabelFor(edge).values)}
                  </div>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <ConfidenceCell edge={edge} />
                </td>
                <td>
                  {formatInstant(edge.valid_from)}
                  {edge.valid_to != null && (
                    /* A closed window means the assertion stopped being true, which is a different
                       fact from never having been made. `lineage.edge` is append-only precisely so
                       that distinction survives. */
                    <div className="lq-dim">
                      {t('lineage.native.closed', { at: formatInstant(edge.valid_to) })}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </DataTable>
      )}
    </Card>
  );
}
